// Memory Leak Fixer
// This utility provides specific fixes for the identified memory leaks

export class MemoryLeakFixer {
  private static instance: MemoryLeakFixer;
  private cleanupFunctions: (() => void)[] = [];
  private isInitialized = false;

  private constructor() {
    this.initializeFixes();
  }

  public static getInstance(): MemoryLeakFixer {
    if (!MemoryLeakFixer.instance) {
      MemoryLeakFixer.instance = new MemoryLeakFixer();
    }
    return MemoryLeakFixer.instance;
  }

  private initializeFixes() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    console.log('🔧 Memory Leak Fixer initializing...');

    // Fix 1: Clean up orphaned DOM nodes
    this.fixDOMNodeLeaks();
    
    // Fix 2: Clean up event listeners
    this.fixEventListenerLeaks();
    
    // Fix 3: Clean up timers
    this.fixTimerLeaks();
    
    // Fix 4: Clean up global references
    this.fixGlobalReferenceLeaks();

    console.log('✅ Memory Leak Fixer initialized with cleanup functions');
  }

  private fixDOMNodeLeaks() {
    // Enhanced DOM cleanup to fix +577 node leaks
    const cleanupDOM = () => {
      let removedCount = 0;

      try {
        // 1. Remove orphaned elements with specific markers
        const orphanedElements = document.querySelectorAll('[data-orphaned="true"]');
        orphanedElements.forEach(el => {
          el.remove();
          removedCount++;
        });

        // 2. Clean up temporary elements
        const tempElements = document.querySelectorAll('.temp-memory-cleanup, .temp-cleanup, [data-temp="true"]');
        tempElements.forEach(el => {
          el.remove();
          removedCount++;
        });

                 // 3. Remove duplicate elements (common in React re-renders) - DISABLED
                 // This was too aggressive and was removing active React elements
                 // const allElements = document.querySelectorAll('*');
                 // const elementMap = new Map<string, Element[]>();
                 // 
                 // allElements.forEach(el => {
                 //   const key = `${el.tagName}-${el.className}-${el.id}`;
                 //   if (!elementMap.has(key)) {
                 //     elementMap.set(key, []);
                 //   }
                 //   elementMap.get(key)!.push(el);
                 // });
                 // 
                 // // Remove duplicates (keep only the first one)
                 // elementMap.forEach((elements, key) => {
                 //   if (elements.length > 1) {
                 //     elements.slice(1).forEach(el => {
                 //       if (el.parentNode) {
                 //         el.remove();
                 //         removedCount++;
                 //       }
                 //     });
                 //   }
                 // });

        // 4. Clean up empty containers - DISABLED
        // This was too aggressive and was removing React elements
        // const emptyContainers = document.querySelectorAll('div:empty, span:empty, p:empty');
        // emptyContainers.forEach(el => {
        //   if (el.children.length === 0 && !el.textContent?.trim()) {
        //     el.remove();
        //     removedCount++;
        //   }
        // });

        // 5. Remove elements with no visible content - DISABLED
        // This was too aggressive and was removing React elements
        // const invisibleElements = document.querySelectorAll('[style*="display: none"], [style*="visibility: hidden"]');
        // invisibleElements.forEach(el => {
        //   if (el.children.length === 0 && !el.textContent?.trim()) {
        //     el.remove();
        //     removedCount++;
        //   }
        // });

        // 6. Clean up React portals and detached elements - DISABLED
        // This was too aggressive and was removing active React elements
        // const reactRoots = document.querySelectorAll('#__next, #root, [data-reactroot]');
        // reactRoots.forEach(root => {
        //   const detachedElements = root.querySelectorAll('*');
        //   detachedElements.forEach(el => {
        //     if (!document.body.contains(el) && el.parentNode === null) {
        //       el.remove();
        //       removedCount++;
        //     }
        //   });
        // });

        if (removedCount > 0) {
          console.log(`🧹 DOM Cleanup: Removed ${removedCount} orphaned/duplicate nodes`);
        }

        // Force garbage collection if available
        if (typeof window !== 'undefined' && 'gc' in window) {
          (window as Window & { gc?: () => void }).gc?.();
        }
      } catch (error) {
        console.error('Error during DOM cleanup:', error);
      }
    };

             // Run cleanup every 30 seconds (less aggressive to avoid removing active DOM elements)
             const interval = setInterval(cleanupDOM, 30000);
    this.cleanupFunctions.push(() => clearInterval(interval));

    // Also run on page unload and visibility change
    window.addEventListener('beforeunload', cleanupDOM);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cleanupDOM();
      }
    });
    
    this.cleanupFunctions.push(() => {
      window.removeEventListener('beforeunload', cleanupDOM);
      document.removeEventListener('visibilitychange', cleanupDOM);
    });
  }

  private fixEventListenerLeaks() {
    // Track event listeners to prevent leaks
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const originalRemoveEventListener = EventTarget.prototype.removeEventListener;
    
    const eventListenerMap = new Map<string, Set<EventListener>>();

    EventTarget.prototype.addEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
      const self = (typeof this === 'undefined' || this === null) ? (typeof window !== 'undefined' ? window : undefined) : this;
      const ctorName = (self as EventTarget)?.constructor?.name || (self === window ? 'Window' : self === document ? 'Document' : 'GlobalEventTarget');
      const key = `${ctorName}-${type}`;
      if (!eventListenerMap.has(key)) {
        eventListenerMap.set(key, new Set());
      }
      eventListenerMap.get(key)!.add(listener as EventListener);
      const context = (self ?? this) as EventTarget;
      return originalAddEventListener.call(context, type, listener, options);
    };

    EventTarget.prototype.removeEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions) {
      const self = (typeof this === 'undefined' || this === null) ? (typeof window !== 'undefined' ? window : undefined) : this;
      const ctorName = (self as EventTarget)?.constructor?.name || (self === window ? 'Window' : self === document ? 'Document' : 'GlobalEventTarget');
      const key = `${ctorName}-${type}`;
      if (eventListenerMap.has(key)) {
        eventListenerMap.get(key)!.delete(listener as EventListener);
        if (eventListenerMap.get(key)!.size === 0) {
          eventListenerMap.delete(key);
        }
      }
      const context = (self ?? this) as EventTarget;
      return originalRemoveEventListener.call(context, type, listener, options);
    };

    // Enhanced event listener cleanup to fix +349 listener leaks
    const cleanupEventListeners = () => {
      try {
        const totalListeners = Array.from(eventListenerMap.values()).reduce((sum, set) => sum + set.size, 0);
        
        if (totalListeners > 50) {
          console.warn(`⚠️ High event listener count detected: ${totalListeners}`);
          
          // Aggressive cleanup: remove listeners from detached elements
          const allElements = document.querySelectorAll('*');
          allElements.forEach(element => {
            // Check if element is detached from DOM
            if (!document.body.contains(element)) {
              // Try to remove all possible event listeners
              const eventTypes = ['click', 'change', 'input', 'focus', 'blur', 'mouseover', 'mouseout', 'scroll', 'resize'];
              eventTypes.forEach(eventType => {
                const key = `${element.constructor.name}-${eventType}`;
                if (eventListenerMap.has(key)) {
                  const listeners = eventListenerMap.get(key)!;
                  listeners.forEach(listener => {
                    try {
                      element.removeEventListener(eventType, listener);
                    } catch {
                      // Ignore errors for detached elements
                    }
                  });
                  listeners.clear();
                }
              });
            }
          });

          // Clean up empty listener sets
          for (const [key, set] of eventListenerMap.entries()) {
            if (set.size === 0) {
              eventListenerMap.delete(key);
            }
          }

          const newTotalListeners = Array.from(eventListenerMap.values()).reduce((sum, set) => sum + set.size, 0);
          if (newTotalListeners < totalListeners) {
            console.log(`🧹 Event Listener Cleanup: Removed ${totalListeners - newTotalListeners} orphaned listeners`);
          }
        }
      } catch (error) {
        console.error('Error during event listener cleanup:', error);
      }
    };

             // Run cleanup every 30 seconds (less aggressive)
             const interval = setInterval(cleanupEventListeners, 30000);
    this.cleanupFunctions.push(() => clearInterval(interval));
  }

  private fixTimerLeaks() {
    // Track timers to prevent leaks
    const originalSetTimeout = window.setTimeout;
    const originalSetInterval = window.setInterval;
    const originalClearTimeout = window.clearTimeout;
    const originalClearInterval = window.clearInterval;
    
    const activeTimers = new Set<number>();
    const activeIntervals = new Set<number>();

    window.setTimeout = ((callback: () => void, delay: number, ...args: unknown[]) => {
      const id = originalSetTimeout(callback, delay, ...args);
      activeTimers.add(id);
      
      // Auto-cleanup after delay + buffer
      originalSetTimeout(() => {
        activeTimers.delete(id);
      }, delay + 1000);
      
      return id;
    }) as typeof setTimeout;

    window.setInterval = ((callback: () => void, delay: number, ...args: unknown[]) => {
      const id = originalSetInterval(callback, delay, ...args);
      activeIntervals.add(id);
      return id;
    }) as typeof setInterval;

    window.clearTimeout = ((id: number) => {
      activeTimers.delete(id);
      return originalClearTimeout(id);
    }) as typeof clearTimeout;

    window.clearInterval = ((id: number) => {
      activeIntervals.delete(id);
      return originalClearInterval(id);
    }) as typeof clearInterval;

    // Enhanced timer cleanup to fix +47 timer leaks
    const cleanupTimers = () => {
      try {
        const totalTimers = activeTimers.size + activeIntervals.size;
        
        if (totalTimers > 10) {
          console.warn(`⚠️ High timer count detected: ${totalTimers} (${activeTimers.size} timeouts, ${activeIntervals.size} intervals)`);
          
          // Aggressive cleanup: clear old timers that might be stuck
          // Clear old timeouts
          const oldTimeouts = Array.from(activeTimers);
          
          oldTimeouts.forEach(id => {
            try {
              originalClearTimeout(id);
              activeTimers.delete(id);
            } catch {
              // Ignore errors for invalid timer IDs
            }
          });

          // Clear old intervals
          const oldIntervals = Array.from(activeIntervals);
          
          oldIntervals.forEach(id => {
            try {
              originalClearInterval(id);
              activeIntervals.delete(id);
            } catch {
              // Ignore errors for invalid timer IDs
            }
          });

          const newTotalTimers = activeTimers.size + activeIntervals.size;
          if (newTotalTimers < totalTimers) {
            console.log(`🧹 Timer Cleanup: Cleared ${totalTimers - newTotalTimers} orphaned timers`);
          }
        }
      } catch (error) {
        console.error('Error during timer cleanup:', error);
      }
    };

             // Run cleanup every 30 seconds (less aggressive)
             const interval = setInterval(cleanupTimers, 30000);
    this.cleanupFunctions.push(() => clearInterval(interval));
  }

  private fixGlobalReferenceLeaks() {
    // Clean up global references that might be holding onto memory
    const cleanupGlobalReferences = () => {
      // Clear any global caches that might be growing
      if (typeof window !== 'undefined') {
        // Clear any global arrays that might be growing
        const globalArrays = ['tempData', 'cachedData', 'memoryCache'];
        globalArrays.forEach(key => {
          const w = window as unknown as Record<string, unknown>;
          if (w[key] && Array.isArray(w[key])) {
            (w[key] as unknown[]).length = 0;
          }
        });

        // Clear any global objects that might be growing
        const globalObjects = ['tempCache', 'memoryStore', 'dataCache'];
        globalObjects.forEach(key => {
          const w = window as unknown as Record<string, unknown>;
          if (w[key] && typeof w[key] === 'object' && w[key] !== null) {
            Object.keys(w[key] as Record<string, unknown>).forEach(k => {
              delete (w[key] as Record<string, unknown>)[k];
            });
          }
        });
      }
    };

    const interval = setInterval(cleanupGlobalReferences, 15000);
    this.cleanupFunctions.push(() => clearInterval(interval));
  }

  public forceCleanup() {
    console.log('🧹 Forcing memory cleanup...');
    
    // Run all cleanup functions
    this.cleanupFunctions.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    });

    // Force garbage collection
    if (typeof window !== 'undefined' && 'gc' in window) {
      (window as Window & { gc?: () => void }).gc?.();
      console.log('✅ Garbage collection forced');
    }

    // Clear any remaining global references
    this.clearGlobalReferences();
  }

  private clearGlobalReferences() {
    // Clear common global references that might cause leaks
    const globalKeys = [
      'tempData', 'cachedData', 'memoryCache', 'tempCache', 
      'memoryStore', 'dataCache', 'componentCache', 'renderCache'
    ];

    globalKeys.forEach(key => {
      const w = window as unknown as Record<string, unknown>;
      if (typeof window !== 'undefined' && w[key]) {
        delete w[key];
      }
    });
  }

  public getCleanupStatus() {
    return {
      cleanupFunctionsCount: this.cleanupFunctions.length,
      isInitialized: this.isInitialized
    };
  }

  public destroy() {
    this.cleanupFunctions.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    });
    this.cleanupFunctions = [];
    this.isInitialized = false;
    console.log('🗑️ Memory Leak Fixer destroyed');
  }
}

// Initialize the fixer - ONLY in development to avoid production overhead and interference
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  MemoryLeakFixer.getInstance();
}
